import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trophy, Flame, Users, Crown, Medal, Award, TrendingUp, UserPlus, X, Copy, Share2, Check, MessageCircle, Ban, CheckCircle, XCircle, Dumbbell, Eye, EyeOff } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { TIER_COLORS, calculateTier, EvolveTier } from "@/types/evolve";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useFriendships } from "@/hooks/useFriendships";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useReferral } from "@/hooks/useReferral";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { FriendProfileModal } from "@/components/FriendProfileModal";

const TwitterIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

interface Big3Lifts {
  bench: number;
  squat: number;
  deadlift: number;
}

interface LeaderboardEntry {
  id: string;
  name: string;
  total_workouts: number;
  current_tier: string;
  badge_count: number;
  avatar_url?: string | null;
  big3_total: number;
  big3_lifts: Big3Lifts;
}

interface LeaderboardModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUserId?: string;
  currentTotalWorkouts: number;
  savedWeightUnit?: 'lbs' | 'kg';
  onUpdateWeightUnit?: (unit: 'lbs' | 'kg') => void;
}

export function LeaderboardModal({ 
  isOpen, 
  onClose, 
  currentUserId,
  currentTotalWorkouts,
  savedWeightUnit = 'lbs',
  onUpdateWeightUnit
}: LeaderboardModalProps) {
  const [leaders, setLeaders] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [userRank, setUserRank] = useState<number | null>(null);
  const [totalUsers, setTotalUsers] = useState(0);
  const [friendCode, setFriendCode] = useState('');
  const [addingFriend, setAddingFriend] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'friends' | 'big3'>('all');
  const [big3SortBy, setBig3SortBy] = useState<'total' | 'bench' | 'squat' | 'deadlift'>('total');
  const [weightUnit, setWeightUnit] = useState<'lbs' | 'kg'>(savedWeightUnit);
  const [copied, setCopied] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState<{ id: string; name: string; avatar: string | null } | null>(null);
  const [userPrivacy, setUserPrivacy] = useState<'full_name' | 'nickname' | 'anonymous' | 'hidden'>('full_name');
  const [updatingPrivacy, setUpdatingPrivacy] = useState(false);
  const [userName, setUserName] = useState<string>('');
  const [userDisplayName, setUserDisplayName] = useState<string>('');
  const [editingNickname, setEditingNickname] = useState(false);
  const [nicknameInput, setNicknameInput] = useState('');

  // Weight conversion helper
  const convertWeight = (lbs: number): number => {
    if (weightUnit === 'kg') return Math.round(lbs * 0.453592);
    return lbs;
  };

  // Toggle weight unit and save to profile
  const toggleWeightUnit = () => {
    const newUnit = weightUnit === 'lbs' ? 'kg' : 'lbs';
    setWeightUnit(newUnit);
    onUpdateWeightUnit?.(newUnit);
  };

  // Sync with saved preference
  useEffect(() => {
    setWeightUnit(savedWeightUnit);
  }, [savedWeightUnit]);

  // Fetch user's privacy setting and names
  useEffect(() => {
    const fetchPrivacyAndNames = async () => {
      if (!currentUserId || !isOpen) return;
      const { data } = await supabase
        .from('profiles')
        .select('leaderboard_privacy, name, display_name')
        .eq('id', currentUserId)
        .single();
      if (data) {
        setUserPrivacy((data.leaderboard_privacy || 'full_name') as typeof userPrivacy);
        setUserName(data.name || '');
        setUserDisplayName(data.display_name || '');
        setNicknameInput(data.display_name || '');
      }
    };
    fetchPrivacyAndNames();
  }, [currentUserId, isOpen]);

  // Update privacy setting
  const updatePrivacy = async (newPrivacy: typeof userPrivacy) => {
    if (!currentUserId) return;
    setUpdatingPrivacy(true);
    const { error } = await supabase
      .from('profiles')
      .update({ leaderboard_privacy: newPrivacy })
      .eq('id', currentUserId);
    
    if (error) {
      toast.error('Failed to update privacy setting');
    } else {
      setUserPrivacy(newPrivacy);
      toast.success('Privacy setting updated');
    }
    setUpdatingPrivacy(false);
  };

  // Update nickname/display name
  const saveNickname = async () => {
    if (!currentUserId) return;
    setUpdatingPrivacy(true);
    const { error } = await supabase
      .from('profiles')
      .update({ display_name: nicknameInput.trim() })
      .eq('id', currentUserId);
    
    if (error) {
      toast.error('Failed to update nickname');
    } else {
      setUserDisplayName(nicknameInput.trim());
      setEditingNickname(false);
      toast.success('Nickname updated');
    }
    setUpdatingPrivacy(false);
  };

  // Get preview name based on privacy setting
  const getPreviewName = (): string => {
    switch (userPrivacy) {
      case 'nickname':
        return userDisplayName || 'Set a nickname...';
      case 'anonymous':
        return 'Anonymous Evolver';
      case 'hidden':
        return '(Not visible)';
      case 'full_name':
      default:
        return userName || 'Evolver';
    }
  };

  const { 
    friends, 
    friendIds, 
    pendingRequests,
    outgoingRequests,
    sendFriendRequest, 
    acceptRequest,
    rejectRequest,
    blockUser,
    removeFriend, 
    loading: friendsLoading 
  } = useFriendships(currentUserId);
  const { referralCode } = useReferral();

  const shareText = `Add me as a friend on Evolve AI! My code: ${referralCode}`;

  const copyMyCode = async () => {
    if (!referralCode) return;
    await navigator.clipboard.writeText(referralCode);
    setCopied(true);
    toast.success('Code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareToTwitter = () => {
    const text = encodeURIComponent(shareText);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank', 'width=550,height=420');
  };

  const shareToWhatsApp = () => {
    const text = encodeURIComponent(shareText);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareViaSMS = () => {
    window.open(`sms:?body=${encodeURIComponent(shareText)}`, '_self');
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ text: shareText });
      } catch (e) {
        // User cancelled
      }
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const fetchLeaderboard = async () => {
      setLoading(true);
      try {
        // Get aggregated workout counts using RPC function (bypasses RLS)
        const { data: workoutCounts, error: countError } = await supabase
          .rpc('get_leaderboard_workout_counts');

        if (countError) throw countError;

        // Build workout map
        const userWorkoutMap = new Map<string, number>();
        workoutCounts?.forEach((row: { user_id: string; total_workouts: number }) => {
          userWorkoutMap.set(row.user_id, row.total_workouts);
        });

        // Get ALL users using security definer function (bypasses RLS for leaderboard)
        const { data: allProfiles, error: profileError } = await supabase
          .rpc('get_leaderboard_profiles');

        if (profileError) {
          console.error('Error fetching leaderboard profiles:', profileError);
          throw profileError;
        }

        console.log('[Leaderboard] Fetched profiles:', allProfiles?.length || 0);

        // Build maps from the profiles data
        const prsMap = new Map<string, any>();
        const privacyMap = new Map<string, string>();
        
        allProfiles?.forEach((p: any) => {
          if (p.id) {
            prsMap.set(p.id, p.prs);
            privacyMap.set(p.id, p.leaderboard_privacy || 'full_name');
          }
        });

        // Get all user IDs from profiles
        const allUserIds = (allProfiles || []).map((p: any) => p.id).filter(Boolean) as string[];

        // Get badge counts for all users
        const { data: badgeCounts } = await supabase
          .from('user_badges')
          .select('user_id')
          .in('user_id', allUserIds.length > 0 ? allUserIds : ['00000000-0000-0000-0000-000000000000']);

        const badgeCountMap = new Map<string, number>();
        badgeCounts?.forEach(b => {
          badgeCountMap.set(b.user_id, (badgeCountMap.get(b.user_id) || 0) + 1);
        });

        // Helper to extract individual Big 3 lifts and total
        const extractBig3 = (prs: any): { lifts: Big3Lifts; total: number } => {
          const lifts: Big3Lifts = { bench: 0, squat: 0, deadlift: 0 };
          if (!Array.isArray(prs)) return { lifts, total: 0 };
          
          prs.forEach((pr: any) => {
            const liftName = pr.lift?.toLowerCase() || '';
            const weight = pr.weight || 0;
            if (liftName.includes('bench')) lifts.bench = Math.max(lifts.bench, weight);
            else if (liftName.includes('squat')) lifts.squat = Math.max(lifts.squat, weight);
            else if (liftName.includes('deadlift')) lifts.deadlift = Math.max(lifts.deadlift, weight);
          });
          
          return { lifts, total: lifts.bench + lifts.squat + lifts.deadlift };
        };

        // Helper to get display name based on privacy setting
        const getDisplayName = (profile: any): string => {
          const privacy = privacyMap.get(profile.id) || 'full_name';
          switch (privacy) {
            case 'nickname':
              return profile.display_name || 'Anonymous Evolver';
            case 'anonymous':
              return 'Anonymous Evolver';
            case 'full_name':
            default:
              return profile.name || 'Evolver';
          }
        };

        // Combine data and sort by workout count
        const leaderboardData: LeaderboardEntry[] = (allProfiles || [])
          .filter((profile: any) => profile.id && privacyMap.get(profile.id) !== 'hidden')
          .map((profile: any) => {
            const prs = prsMap.get(profile.id!) || [];
            const big3Data = extractBig3(prs);
            return {
              id: profile.id || '',
              name: getDisplayName(profile),
              total_workouts: userWorkoutMap.get(profile.id || '') || 0,
              current_tier: profile.current_tier || 'Seed',
              badge_count: badgeCountMap.get(profile.id || '') || 0,
              avatar_url: profile.avatar_url,
              big3_total: big3Data.total,
              big3_lifts: big3Data.lifts,
            };
          })
          .sort((a, b) => b.total_workouts - a.total_workouts)
          .slice(0, 50);

        setLeaders(leaderboardData);

        // Find current user's rank
        if (currentUserId) {
          const rank = leaderboardData.findIndex(u => u.id === currentUserId);
          if (rank >= 0) {
            setUserRank(rank + 1);
            setTotalUsers(leaderboardData.length);
          } else {
            const usersWithMoreWorkouts = leaderboardData.filter(
              u => u.total_workouts > currentTotalWorkouts
            ).length;
            setUserRank(usersWithMoreWorkouts + 1);
            setTotalUsers(leaderboardData.length + 1);
          }
        } else {
          setTotalUsers(leaderboardData.length);
        }
      } catch (error) {
        console.error('Error fetching leaderboard:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [isOpen, currentUserId, currentTotalWorkouts]);

  const handleAddFriend = async () => {
    if (!friendCode.trim()) return;
    setAddingFriend(true);
    const success = await sendFriendRequest(friendCode.trim());
    if (success) {
      setFriendCode('');
    }
    setAddingFriend(false);
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
    if (rank === 3) return <Award className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 text-center text-muted-foreground text-sm">#{rank}</span>;
  };

  const tierInfo = calculateTier(currentTotalWorkouts);
  
  // Filter and sort leaders based on active tab
  const filteredLeaders = (() => {
    let filtered = activeTab === 'friends' 
      ? leaders.filter(l => friendIds.includes(l.id) || l.id === currentUserId)
      : leaders;
    
    // Sort by selected metric for the big3 tab
    if (activeTab === 'big3') {
      filtered = [...filtered].sort((a, b) => {
        switch (big3SortBy) {
          case 'bench': return b.big3_lifts.bench - a.big3_lifts.bench;
          case 'squat': return b.big3_lifts.squat - a.big3_lifts.squat;
          case 'deadlift': return b.big3_lifts.deadlift - a.big3_lifts.deadlift;
          default: return b.big3_total - a.big3_total;
        }
      });
    }
    
    return filtered;
  })();

  // Calculate friend rank
  const friendRank = activeTab === 'friends' && currentUserId
    ? filteredLeaders.findIndex(u => u.id === currentUserId) + 1
    : null;
  
  // Calculate big3 rank
  const big3Rank = activeTab === 'big3' && currentUserId
    ? filteredLeaders.findIndex(u => u.id === currentUserId) + 1
    : null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-hidden flex flex-col bg-card">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Trophy className="w-5 h-5 text-primary" />
              Evolver Leaderboard
            </DialogTitle>
          </DialogHeader>

          {/* Your Stats Card */}
          <div className="p-4 rounded-xl bg-primary/10 border border-primary/30 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Flame className="w-5 h-5 text-primary" />
                <span className="font-medium">Your Rank</span>
              </div>
              <div className="flex items-center gap-2">
                {(activeTab === 'friends' ? friendRank : activeTab === 'big3' ? big3Rank : userRank) && (
                  <span className="font-display text-2xl font-bold text-primary">
                    #{activeTab === 'friends' ? friendRank : activeTab === 'big3' ? big3Rank : userRank}
                  </span>
                )}
                <span className="text-xs text-muted-foreground">
                  of {activeTab === 'friends' || activeTab === 'big3' ? filteredLeaders.length : totalUsers}
                </span>
              </div>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <div>
                <span className={cn(
                  "px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r",
                  TIER_COLORS[tierInfo.currentTier as EvolveTier] || TIER_COLORS['Seed']
                )}>
                  {tierInfo.currentTier}
                </span>
              </div>
              <div className="text-right">
                <span className="font-bold">{currentTotalWorkouts}</span>
                <span className="text-muted-foreground ml-1">workouts</span>
              </div>
            </div>

            {tierInfo.workoutsToNext && tierInfo.nextTierName && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <TrendingUp className="w-3 h-3" />
                <span>{tierInfo.workoutsToNext} more workouts to <span className="text-primary">{tierInfo.nextTierName}</span></span>
              </div>
            )}

            {/* Privacy Setting */}
            <div className="pt-2 border-t border-border/50 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {userPrivacy === 'hidden' ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  <span>Visibility:</span>
                </div>
                <Select
                  value={userPrivacy}
                  onValueChange={(v) => updatePrivacy(v as typeof userPrivacy)}
                  disabled={updatingPrivacy}
                >
                  <SelectTrigger className="w-[130px] h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_name">Full Name</SelectItem>
                    <SelectItem value="nickname">Nickname</SelectItem>
                    <SelectItem value="anonymous">Anonymous</SelectItem>
                    <SelectItem value="hidden">Hidden</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Preview */}
              <div className="flex items-center justify-between bg-secondary/30 rounded-md px-2 py-1.5">
                <span className="text-xs text-muted-foreground">Preview:</span>
                <span className={cn(
                  "text-xs font-medium",
                  userPrivacy === 'hidden' && "text-muted-foreground italic",
                  userPrivacy === 'nickname' && !userDisplayName && "text-muted-foreground italic"
                )}>
                  {getPreviewName()}
                </span>
              </div>

              {/* Nickname Editor */}
              {userPrivacy === 'nickname' && (
                <div className="space-y-1.5">
                  {editingNickname ? (
                    <div className="flex gap-2">
                      <Input
                        value={nicknameInput}
                        onChange={(e) => setNicknameInput(e.target.value)}
                        placeholder="Enter nickname..."
                        className="h-7 text-xs flex-1"
                        maxLength={20}
                      />
                      <Button 
                        size="sm" 
                        className="h-7 text-xs px-2"
                        onClick={saveNickname}
                        disabled={updatingPrivacy || !nicknameInput.trim()}
                      >
                        Save
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="h-7 text-xs px-2"
                        onClick={() => {
                          setNicknameInput(userDisplayName);
                          setEditingNickname(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs w-full"
                      onClick={() => setEditingNickname(true)}
                    >
                      {userDisplayName ? 'Edit Nickname' : 'Set Nickname'}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Tabs for All vs Friends vs Big 3 */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'all' | 'friends' | 'big3')} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="all" className="flex items-center gap-1 text-xs px-2">
                <Users className="w-3 h-3" />
                All
              </TabsTrigger>
              <TabsTrigger value="big3" className="flex items-center gap-1 text-xs px-2">
                <Dumbbell className="w-3 h-3" />
                Big 3
              </TabsTrigger>
              <TabsTrigger value="friends" className="flex items-center gap-1 text-xs px-2 relative">
                <UserPlus className="w-3 h-3" />
                Friends
                {pendingRequests.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-primary-foreground text-[10px] rounded-full flex items-center justify-center">
                    {pendingRequests.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="flex-1 overflow-y-auto -mx-6 px-6 mt-4">
              <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
                <Users className="w-4 h-4" />
                <span>Top Evolvers</span>
              </div>
              {renderLeaderboard(false, 'workouts')}
            </TabsContent>

            <TabsContent value="big3" className="flex-1 overflow-y-auto -mx-6 px-6 mt-4">
            {/* Big 3 Header with sort options and unit toggle */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Dumbbell className="w-4 h-4" />
                  <span>Big 3</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {/* Unit Toggle */}
                  <button
                    onClick={toggleWeightUnit}
                    className="text-[10px] px-2 py-1 rounded bg-secondary/50 hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {weightUnit}
                  </button>
                </div>
              </div>
              
              {/* Sort Options */}
              <div className="flex gap-1 mb-3 overflow-x-auto pb-1">
                {(['total', 'bench', 'squat', 'deadlift'] as const).map((option) => (
                  <button
                    key={option}
                    onClick={() => setBig3SortBy(option)}
                    className={cn(
                      "text-[10px] px-2 py-1 rounded whitespace-nowrap transition-colors",
                      big3SortBy === option 
                        ? "bg-primary text-primary-foreground" 
                        : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
                    )}
                  >
                    {option === 'total' ? 'Total' : option.charAt(0).toUpperCase() + option.slice(1)}
                  </button>
                ))}
              </div>
              
              {renderLeaderboard(false, 'big3')}
            </TabsContent>

            <TabsContent value="friends" className="flex-1 overflow-hidden flex flex-col mt-4">
              {/* Your Code - Share with friends */}
              {referralCode && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50 border border-border mb-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Your code</p>
                    <p className="font-mono font-bold text-lg tracking-wider">{referralCode}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={copyMyCode}>
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button size="sm" variant="ghost">
                          <Share2 className="w-4 h-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-48 p-2" align="end">
                        <div className="space-y-1">
                          <button
                            onClick={() => { shareToWhatsApp(); }}
                            className="w-full flex items-center gap-2 p-2 rounded-md hover:bg-secondary text-sm text-left"
                          >
                            <WhatsAppIcon />
                            WhatsApp
                          </button>
                          <button
                            onClick={() => { shareToTwitter(); }}
                            className="w-full flex items-center gap-2 p-2 rounded-md hover:bg-secondary text-sm text-left"
                          >
                            <TwitterIcon />
                            X / Twitter
                          </button>
                          <button
                            onClick={() => { shareViaSMS(); }}
                            className="w-full flex items-center gap-2 p-2 rounded-md hover:bg-secondary text-sm text-left"
                          >
                            <MessageCircle className="w-4 h-4" />
                            SMS / Text
                          </button>
                          {navigator.share && (
                            <button
                              onClick={() => { nativeShare(); }}
                              className="w-full flex items-center gap-2 p-2 rounded-md hover:bg-secondary text-sm text-left"
                            >
                              <Share2 className="w-4 h-4" />
                              More Options...
                            </button>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              )}

              {/* Pending Friend Requests */}
              {pendingRequests.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                    <UserPlus className="w-3 h-3" />
                    Friend Requests ({pendingRequests.length})
                  </p>
                  <div className="space-y-2">
                    {pendingRequests.map((request) => (
                      <div
                        key={request.id}
                        className="flex items-center justify-between p-2 rounded-lg bg-primary/10 border border-primary/30"
                      >
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={request.friendAvatar || undefined} />
                            <AvatarFallback>{request.friendName[0]?.toUpperCase()}</AvatarFallback>
                          </Avatar>
                          <span className="text-sm font-medium">{request.friendName}</span>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-green-500 hover:text-green-400 hover:bg-green-500/20"
                            onClick={() => acceptRequest(request.id)}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                            onClick={() => rejectRequest(request.id)}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/20"
                            onClick={() => blockUser(request.id, request.friendId)}
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Outgoing Requests */}
              {outgoingRequests.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-muted-foreground mb-2">
                    Pending ({outgoingRequests.length})
                  </p>
                  <div className="space-y-2">
                    {outgoingRequests.map((request) => (
                      <div
                        key={request.id}
                        className="flex items-center justify-between p-2 rounded-lg bg-secondary/30"
                      >
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={request.friendAvatar || undefined} />
                            <AvatarFallback>{request.friendName[0]?.toUpperCase()}</AvatarFallback>
                          </Avatar>
                          <div>
                            <span className="text-sm font-medium">{request.friendName}</span>
                            <p className="text-xs text-muted-foreground">Waiting for response...</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add Friend Input */}
              <div className="flex gap-2 mb-4">
                <Input
                  placeholder="Enter friend's code"
                  value={friendCode}
                  onChange={(e) => setFriendCode(e.target.value.toUpperCase())}
                  className="text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddFriend()}
                />
                <Button 
                  size="sm" 
                  onClick={handleAddFriend}
                  disabled={addingFriend || !friendCode.trim()}
                >
                  {addingFriend ? '...' : 'Send'}
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto -mx-6 px-6">
                {friends.length === 0 && pendingRequests.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <UserPlus className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No friends yet!</p>
                    <p className="text-sm mt-1">Share your code above to add friends</p>
                  </div>
                ) : friends.length > 0 && (
                  renderLeaderboard(true, 'workouts')
                )}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Friend Profile Modal */}
      {selectedFriend && (
        <FriendProfileModal
          isOpen={!!selectedFriend}
          onClose={() => setSelectedFriend(null)}
          friendId={selectedFriend.id}
          friendName={selectedFriend.name}
          friendAvatar={selectedFriend.avatar}
        />
      )}
    </>
  );

  function renderLeaderboard(showRemove: boolean, sortType: 'workouts' | 'big3' = 'workouts') {
    if (loading || friendsLoading) {
      return (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-14 rounded-lg" />
          ))}
        </div>
      );
    }

    if (filteredLeaders.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No evolvers yet.</p>
          <p className="text-sm">You're the first! 🔥</p>
        </div>
      );
    }

    return (
      <div className="space-y-2 pb-4">
        {filteredLeaders.map((leader, index) => {
          const rank = index + 1;
          const isCurrentUser = leader.id === currentUserId;
          const leaderTier = calculateTier(leader.total_workouts || 0);
          const friendship = friends.find(f => f.friendId === leader.id);
          const isFriend = friendIds.includes(leader.id);
          
          return (
            <div
              key={leader.id}
              className={cn(
                "flex items-center gap-3 p-3 rounded-lg transition-all",
                isCurrentUser 
                  ? "bg-primary/10 border border-primary/30" 
                  : "bg-secondary/30 hover:bg-secondary/50",
                isFriend && !isCurrentUser && "cursor-pointer"
              )}
              onClick={() => {
                if (isFriend && !isCurrentUser) {
                  setSelectedFriend({
                    id: leader.id,
                    name: leader.name,
                    avatar: friendship?.friendAvatar || null
                  });
                }
              }}
            >
              <div className="w-8 flex justify-center">
                {getRankIcon(rank)}
              </div>

              {/* Avatar */}
              <Avatar className="h-9 w-9 border border-border">
                <AvatarImage src={leader.avatar_url || undefined} />
                <AvatarFallback className="text-xs">{leader.name?.[0]?.toUpperCase() || 'E'}</AvatarFallback>
              </Avatar>
              
              <div className="flex-1 min-w-0">
                <p className={cn(
                  "font-medium truncate text-sm",
                  isCurrentUser && "text-primary"
                )}>
                  {leader.name || 'Evolver'}
                  {isCurrentUser && <span className="text-xs ml-1 opacity-70">(You)</span>}
                </p>
              </div>
              
              <div className="text-right flex items-center gap-2">
                {sortType === 'big3' ? (
                  <div>
                    <p className="font-bold text-lg">
                      {big3SortBy === 'total' && `${convertWeight(leader.big3_total) || 0}`}
                      {big3SortBy === 'bench' && `${convertWeight(leader.big3_lifts.bench) || 0}`}
                      {big3SortBy === 'squat' && `${convertWeight(leader.big3_lifts.squat) || 0}`}
                      {big3SortBy === 'deadlift' && `${convertWeight(leader.big3_lifts.deadlift) || 0}`}
                      <span className="text-xs font-normal">{weightUnit}</span>
                    </p>
                    <p className="text-xs text-muted-foreground">{big3SortBy}</p>
                  </div>
                ) : (
                  <div>
                    <p className="font-bold text-lg">{leader.total_workouts || 0}</p>
                    <p className="text-xs text-muted-foreground">workouts</p>
                  </div>
                )}
                {showRemove && !isCurrentUser && friendship && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFriend(friendship.id, leader.id);
                    }}
                    className="p-1 rounded hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
}
